<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import Button from "primevue/button";
  import ProgressSpinner from "primevue/progressspinner";
  import AppIcon from "./AppIcon.vue";
  import { supabase } from "../lib/supabase";
  import { useRapportDocumentVereisten } from "../composables/useRapportDocumentVereisten";
  import { useToast } from "../composables/useToast";
  import { useConfirm } from "primevue/useconfirm";
  import type { InspectieType, RapportDocumentUpload } from "../../../../types";

  const props = defineProps<{
    rapportId: string;
    activeInspectieType?: InspectieType | null;
  }>();

  const toast = useToast();
  const confirm = useConfirm();
  const api = useRapportDocumentVereisten();

  const currentUserId = ref<string | null>(null);
  onMounted(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    currentUserId.value = user?.id || null;
  });

  const load = () => api.load(props.rapportId);
  onMounted(load);
  watch(() => props.rapportId, load);

  // Items for the active inspectie type plus rapport-wide (NULL-type) items.
  const visibleVereisten = computed(() =>
    api.vereisten.value.filter(
      (item) => item.inspectie_type === null || !props.activeInspectieType || item.inspectie_type === props.activeInspectieType,
    ),
  );

  const uploadsByVereiste = computed(() => {
    const map = new Map<string | null, RapportDocumentUpload[]>();
    for (const upload of api.uploads.value) {
      const key = upload.vereiste_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(upload);
    }
    return map;
  });

  const extraUploads = computed(() => uploadsByVereiste.value.get(null) || []);

  const hasContent = computed(() => visibleVereisten.value.length > 0 || api.uploads.value.length > 0);

  const openstaandVereist = computed(
    () =>
      visibleVereisten.value.filter((item) => item.vereist && !(uploadsByVereiste.value.get(item.id) || []).length)
        .length,
  );

  // Shared hidden file input; pendingVereisteId tracks the upload target.
  const fileInput = ref<HTMLInputElement | null>(null);
  const pendingVereisteId = ref<string | null>(null);
  const uploadingFor = ref<string | null>(null);

  const startUpload = (vereisteId: string | null) => {
    pendingVereisteId.value = vereisteId;
    fileInput.value?.click();
  };

  const onFilesSelected = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = "";
    if (files.length === 0) return;
    const vereisteId = pendingVereisteId.value;
    uploadingFor.value = vereisteId ?? "extra";
    try {
      await api.uploadFiles(props.rapportId, vereisteId, files);
      toast.success(files.length === 1 ? "Document geüpload" : `${files.length} documenten geüpload`);
    } catch (err: any) {
      console.error("Error uploading documents:", err);
      toast.error(err.message || "Document uploaden mislukt", err);
    } finally {
      uploadingFor.value = null;
    }
  };

  const confirmRemoveUpload = (upload: RapportDocumentUpload) => {
    confirm.require({
      message:
        `Weet u zeker dat u het bestand "${upload.file_name}" wilt verwijderen? ` +
        "Dit kan niet ongedaan worden gemaakt.",
      header: "Document verwijderen",
      icon: "pi pi-exclamation-triangle",
      rejectLabel: "Annuleren",
      acceptLabel: "Verwijderen",
      acceptClass: "p-button-danger",
      accept: () => removeUpload(upload),
    });
  };

  const removeUpload = async (upload: RapportDocumentUpload) => {
    try {
      await api.deleteUpload(props.rapportId, upload);
      toast.success("Document verwijderd");
    } catch (err: any) {
      console.error("Error deleting upload:", err);
      toast.error("Document kon niet worden verwijderd", err);
    }
  };

  const openUpload = (upload: RapportDocumentUpload) => {
    window.open(api.getUploadUrl(upload), "_blank", "noopener");
  };

  const canDelete = (upload: RapportDocumentUpload) => upload.geupload_door === currentUserId.value;
</script>

<template>
  <section v-if="hasContent" class="detail-section">
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      multiple
      @change="onFilesSelected"
    />

    <div class="mb-1 flex flex-wrap items-center justify-between gap-2">
      <h3 class="section-title !mb-0">Gevraagde documenten</h3>
      <span
        v-if="openstaandVereist > 0"
        class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-950/50 dark:text-orange-300"
      >
        {{ openstaandVereist }} vereist openstaand
      </span>
    </div>
    <p class="text-background-500 mb-3 text-sm">
      Het inspectiebedrijf vraagt onderstaande documenten aan te leveren vóór de inspectie.
    </p>

    <div v-if="api.loading.value" class="flex justify-center p-4">
      <ProgressSpinner style="width: 2rem; height: 2rem" />
    </div>

    <div v-else class="flex flex-col gap-2">
      <div
        v-for="item in visibleVereisten"
        :key="item.id"
        class="border-background-200 rounded-sm border p-3"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium">{{ item.naam }}</span>
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="
                  (uploadsByVereiste.get(item.id) || []).length
                    ? 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300'
                    : item.vereist
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300'
                      : 'bg-background-100 text-background-600'
                "
              >
                {{
                  (uploadsByVereiste.get(item.id) || []).length
                    ? "Aangeleverd"
                    : item.vereist
                      ? "Vereist"
                      : "Optioneel"
                }}
              </span>
            </div>
            <p v-if="item.omschrijving" class="text-background-500 mt-0.5 text-xs">{{ item.omschrijving }}</p>
          </div>
          <Button
            label="Uploaden"
            size="small"
            severity="secondary"
            outlined
            :loading="uploadingFor === item.id"
            :disabled="api.uploading.value"
            @click="startUpload(item.id)"
          >
            <template #icon><AppIcon name="upload" size="sm" /></template>
          </Button>
        </div>

        <div v-if="(uploadsByVereiste.get(item.id) || []).length" class="mt-2 flex flex-col gap-1">
          <div
            v-for="upload in uploadsByVereiste.get(item.id)"
            :key="upload.id"
            class="bg-background-50 flex items-center gap-2 rounded-sm px-2 py-1"
          >
            <AppIcon name="file" size="sm" class="text-background-500 flex-none" />
            <button
              type="button"
              class="text-primary min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:underline"
              @click="openUpload(upload)"
            >
              {{ upload.file_name }}
            </button>
            <Button
              v-if="canDelete(upload)"
              text
              rounded
              severity="danger"
              size="small"
              aria-label="Verwijderen"
              @click="confirmRemoveUpload(upload)"
            >
              <template #icon><AppIcon name="delete" size="xs" /></template>
            </Button>
          </div>
        </div>
      </div>

      <!-- Extra documents outside the requested list -->
      <div class="border-background-200 rounded-sm border border-dashed p-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span class="text-sm font-medium">Extra documenten</span>
            <p class="text-background-500 mt-0.5 text-xs">
              Overige documenten die relevant kunnen zijn voor de inspectie.
            </p>
          </div>
          <Button
            label="Uploaden"
            size="small"
            severity="secondary"
            outlined
            :loading="uploadingFor === 'extra'"
            :disabled="api.uploading.value"
            @click="startUpload(null)"
          >
            <template #icon><AppIcon name="upload" size="sm" /></template>
          </Button>
        </div>

        <div v-if="extraUploads.length" class="mt-2 flex flex-col gap-1">
          <div
            v-for="upload in extraUploads"
            :key="upload.id"
            class="bg-background-50 flex items-center gap-2 rounded-sm px-2 py-1"
          >
            <AppIcon name="file" size="sm" class="text-background-500 flex-none" />
            <button
              type="button"
              class="text-primary min-w-0 flex-1 cursor-pointer truncate text-left text-sm hover:underline"
              @click="openUpload(upload)"
            >
              {{ upload.file_name }}
            </button>
            <Button
              v-if="canDelete(upload)"
              text
              rounded
              severity="danger"
              size="small"
              aria-label="Verwijderen"
              @click="confirmRemoveUpload(upload)"
            >
              <template #icon><AppIcon name="delete" size="xs" /></template>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  /* Mirrors RapportenDetails' scoped .detail-section/.section-title styling so
     this section blends in when rendered outside that view's scope. */
  .detail-section {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin: 0;
  }
</style>
